import { useState, useEffect } from "react";
import { DollarSign, TrendingUp, Clock, CheckCircle2, Trophy } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function CommissionsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [commissions, setCommissions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [period, setPeriod] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; });
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const fetch = async () => {
    setLoading(true);
    const params = { period };
    if (statusFilter) params.status = statusFilter;
    await Promise.all([
      api.get("/commissions", { params }).then((r) => setCommissions(r.data)),
      api.get("/commissions/summary", { params: { period } }).then((r) => setSummary(r.data)),
    ]).catch(() => {});
    setLoading(false);
  };

  useEffect(() => { fetch(); }, [period, statusFilter]);

  const markPaid = async (id) => {
    await api.put(`/commissions/${id}/pay`);
    fetch();
  };

  return (
    <div data-testid="commissions-page">
      <h1 className="crm-heading mb-8">Comissões</h1>

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <div className="crm-card p-5">
            <div className="flex items-center gap-2 mb-2"><DollarSign className="w-4 h-4" style={{ color: "var(--crm-accent)" }} /><span className="text-xs font-semibold uppercase" style={{ color: "var(--crm-text-muted)" }}>Total</span></div>
            <p className="text-2xl font-bold" style={{ color: "var(--crm-text)" }}>¥{summary.total.toLocaleString()}</p>
          </div>
          <div className="crm-card p-5">
            <div className="flex items-center gap-2 mb-2"><Clock className="w-4 h-4" style={{ color: "#f97316" }} /><span className="text-xs font-semibold uppercase" style={{ color: "var(--crm-text-muted)" }}>Pendente</span></div>
            <p className="text-2xl font-bold" style={{ color: "#f97316" }}>¥{summary.pending.toLocaleString()}</p>
          </div>
          <div className="crm-card p-5">
            <div className="flex items-center gap-2 mb-2"><CheckCircle2 className="w-4 h-4" style={{ color: "#10b981" }} /><span className="text-xs font-semibold uppercase" style={{ color: "var(--crm-text-muted)" }}>Pago</span></div>
            <p className="text-2xl font-bold" style={{ color: "#10b981" }}>¥{summary.paid.toLocaleString()}</p>
          </div>
          <div className="crm-card p-5">
            <div className="flex items-center gap-2 mb-2"><TrendingUp className="w-4 h-4" style={{ color: "#3b82f6" }} /><span className="text-xs font-semibold uppercase" style={{ color: "var(--crm-text-muted)" }}>Registros</span></div>
            <p className="text-2xl font-bold" style={{ color: "var(--crm-text)" }}>{summary.pending_count + summary.paid_count}</p>
          </div>
        </div>
      )}

      {/* Top operators */}
      {isAdmin && summary?.top_operators?.length > 0 && (
        <div className="crm-card p-5 mb-8">
          <h3 className="crm-subheading flex items-center gap-2"><Trophy className="w-4 h-4" style={{ color: "#f59e0b" }} /> Top Operadores</h3>
          <div className="flex flex-wrap gap-3">
            {summary.top_operators.map((op, i) => (
              <div key={op.operator_id} className="flex items-center gap-3 px-4 py-2 rounded-xl" style={{ background: "var(--crm-input-bg)", border: "1px solid var(--crm-border)" }}>
                <span className="text-xs font-bold" style={{ color: i === 0 ? "#f59e0b" : "var(--crm-text-dim)" }}>#{i + 1}</span>
                <span className="text-sm font-medium" style={{ color: "var(--crm-text)" }}>{op.name}</span>
                <span className="text-sm font-bold" style={{ color: "var(--crm-accent)" }}>¥{op.total.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3 mb-6">
        <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="crm-input" style={{ maxWidth: "200px" }} />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="crm-input" style={{ maxWidth: "180px" }}>
          <option value="">Todos</option><option value="pending">Pendente</option><option value="paid">Pago</option>
        </select>
      </div>

      {/* Table */}
      <div className="crm-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="crm-table">
            <thead><tr><th>Operador</th><th>Cliente</th><th>Valor</th><th>Status</th><th>Data</th>{isAdmin && <th className="text-right">Ações</th>}</tr></thead>
            <tbody>
              {loading ? (
                [...Array(3)].map((_, i) => <tr key={i}><td colSpan={6}><div className="h-5 rounded animate-pulse" style={{ background: "var(--crm-border)" }} /></td></tr>)
              ) : commissions.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-14" style={{ color: "var(--crm-text-dim)" }}>Nenhuma comissão encontrada</td></tr>
              ) : commissions.map((c) => (
                <tr key={c.id}>
                  <td className="primary">{c.operator_name}</td>
                  <td>{c.customer_name || "—"}</td>
                  <td style={{ fontWeight: 700, fontSize: "16px", color: "var(--crm-text)" }}>¥{c.amount.toLocaleString()}</td>
                  <td>
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold`}
                      style={{ background: c.status === "paid" ? "rgba(16,185,129,0.12)" : "rgba(249,115,22,0.12)", color: c.status === "paid" ? "#10b981" : "#f97316" }}>
                      {c.status === "paid" ? <><CheckCircle2 className="w-3.5 h-3.5" /> PAGO</> : <><Clock className="w-3.5 h-3.5" /> PENDENTE</>}
                    </span>
                  </td>
                  <td style={{ color: "var(--crm-text-muted)" }}>{new Date(c.created_at).toLocaleDateString("pt-BR")}</td>
                  {isAdmin && (
                    <td className="text-right">
                      {c.status === "pending" && (
                        <button onClick={() => markPaid(c.id)} className="inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-xl transition-all"
                          style={{ color: "white", background: "#10b981", boxShadow: "0 2px 8px rgba(16,185,129,0.25)" }}>
                          <CheckCircle2 className="w-4 h-4" /> Pagar
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
